import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OAuth2TokenResponseModel } from '../models/oauth2-token-response.model';
import { environment } from '../../environments/environment';
import { EmailAndPasswordModel } from '../models/email-password.model';
import { lastValueFrom, BehaviorSubject, tap } from 'rxjs';
import { ResetPasswordModel } from '../models/reset-password.model';
import { VerifyEmailModel } from '../models/verify-email.model';
import { ActivatedRoute, Router } from '@angular/router';
import { RegisterRequestModel } from '../models/ThumperGamesApiModels/request-models/register-request.model';
import { AccountRegisterResponseModel } from '../models/account-register-response.model';
import { UserProfileModel } from '../models/user-profile.model';

export const ThumperGamesRoles = {
  Admin: 'Admin',
  Developer: 'Developer',
  Tester: 'Tester'
} as const;

export type ThumperGamesRole = typeof ThumperGamesRoles[keyof typeof ThumperGamesRoles];

@Injectable({
  providedIn: 'root'
})
export class AuthServiceService {
  baseApiUrl = environment.apiUrl + 'public/'
  oAuthStartUrl = this.baseApiUrl + 'oauth2/start';

  private readonly refreshTokenExpectedStorageKey = 'refresh_token_expected';

  private usernameSubject = new BehaviorSubject<string | null>(null);
  public username$ = this.usernameSubject.asObservable();

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private rolesSubject = new BehaviorSubject<string[]>([]);
  public roles$ = this.rolesSubject.asObservable();

  private pendingEmailBehaviorSubject = new BehaviorSubject<string | null>(null);
  public pendingEmail$ = this.pendingEmailBehaviorSubject.asObservable();

  private refreshInProgress: Promise<boolean> | null = null;
  private readonly webClientHeaders = { 'X-ThumperGames-Client': 'web' };
  private readonly jsonWebClientHeaders = { 'Content-Type': 'application/json', 'X-ThumperGames-Client': 'web' };

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private router: Router
  ) { }

  public async initialize(): Promise<void> {
    const token = sessionStorage.getItem('access_token');
    const expiration = sessionStorage.getItem('token_expiration');
    const isAccessTokenValid = !!token && expiration && Date.now() < parseInt(expiration, 10);

    if (isAccessTokenValid) {
      const username = this.getUsernameFromToken(token!);
      this.usernameSubject.next(username);
      this.rolesSubject.next(this.getRolesFromToken(token));
      this.isAuthenticatedSubject.next(true);
    } else {
      if (!this.hasRefreshTokenExpected()) {
        this.clearStoredAuthState();
        return;
      }

      const refreshed = await this.sendRefreshTokenRequest();
      if (refreshed) {
        const newToken = sessionStorage.getItem('access_token');
        const username = newToken ? this.getUsernameFromToken(newToken) : null;
        this.usernameSubject.next(username);
        this.rolesSubject.next(this.getRolesFromToken(newToken));
        this.isAuthenticatedSubject.next(true);
      } else {
        this.usernameSubject.next(null);
        this.rolesSubject.next([]);
        this.isAuthenticatedSubject.next(false);
      }
    }
  }

  private decodeJwtPayload(token: string): Record<string, unknown> {
    const base64UrlPayload = token.split('.')[1];
    const base64Payload = base64UrlPayload
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(base64UrlPayload.length / 4) * 4, '=');

    return JSON.parse(atob(base64Payload));
  }

  getUserIdFromToken(token: string | null): string | null {
    if (token == null)
      token = sessionStorage.getItem('access_token');
    if (token == null)
      throw 'Token missing';
    try {
      const payload = this.decodeJwtPayload(token);
      return payload?.["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"] as string || null;
    } catch (e) {
      console.error("Token parse error", e);
      throw e;
    }
  }

  getUsernameFromToken(token: string): string | null {
    try {
      const payload = this.decodeJwtPayload(token);
      return payload?.["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"] as string || null;
    } catch (e) {
      console.error("Token parse error", e);
      throw e;
    }
  }

  getEmailFromToken(token: string): string | null {
    try {
      const payload = this.decodeJwtPayload(token);
      return payload?.["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/email"] as string || null;
    } catch (e) {
      console.error("Token parse error", e);
      throw e;
    }
  }

  getRolesFromToken(token: string | null = sessionStorage.getItem('access_token')): string[] {
    if (!token) {
      return [];
    }

    try {
      const payload = this.decodeJwtPayload(token);
      const roleClaim =
        payload?.["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] ??
        payload?.["role"] ??
        payload?.["roles"];

      if (Array.isArray(roleClaim)) {
        return roleClaim.filter((role): role is string => typeof role === 'string');
      }

      if (typeof roleClaim === 'string') {
        return [roleClaim];
      }

      return [];
    } catch (e) {
      console.error("Token parse error", e);
      return [];
    }
  }

  private hasExactRole(role: string): boolean {
    return this.getRolesFromToken().some(userRole => userRole.toLowerCase() === role.toLowerCase());
  }

  hasRole(role: string): boolean {
    if (role.toLowerCase() === ThumperGamesRoles.Admin.toLowerCase()) {
      return this.hasExactRole(ThumperGamesRoles.Admin);
    }

    if (role.toLowerCase() === ThumperGamesRoles.Developer.toLowerCase()) {
      return this.hasExactRole(ThumperGamesRoles.Admin) ||
        this.hasExactRole(ThumperGamesRoles.Developer);
    }

    if (role.toLowerCase() === ThumperGamesRoles.Tester.toLowerCase()) {
      return this.hasExactRole(ThumperGamesRoles.Admin) ||
        this.hasExactRole(ThumperGamesRoles.Developer) ||
        this.hasExactRole(ThumperGamesRoles.Tester);
    }

    return this.hasExactRole(role);
  }

  hasAnyRole(roles: string[]): boolean {
    return roles.some(role => this.hasRole(role));
  }

  isAdmin(): boolean {
    return this.hasExactRole(ThumperGamesRoles.Admin);
  }

  isDeveloper(): boolean {
    return this.hasRole(ThumperGamesRoles.Developer);
  }

  isTester(): boolean {
    return this.hasRole(ThumperGamesRoles.Tester);
  }

  async isAuthenticated(): Promise<boolean> {
    const token = sessionStorage.getItem('access_token');
    const expiration = sessionStorage.getItem('token_expiration');
    let isAccessTokenValid = !!token && expiration !== null && Date.now() < parseInt(expiration, 10);
    if (!isAccessTokenValid) {
      isAccessTokenValid =
        this.hasRefreshTokenExpected() &&
        await this.sendRefreshTokenRequest();
    } else {
      this.rolesSubject.next(this.getRolesFromToken(token));
    }
    console.log('isAuthenticated =', isAccessTokenValid);
    this.isAuthenticatedSubject.next(isAccessTokenValid);
    return isAccessTokenValid;
  }

  async sendRefreshTokenRequest(): Promise<boolean> {
    if (!this.hasRefreshTokenExpected()) {
      this.clearStoredAuthState();
      return false;
    }

    if (this.refreshInProgress) {
      return this.refreshInProgress;
    }
    this.refreshInProgress = (async () => {
      console.log('Access token Refreshing...');
      try {
        const tokenResponse = await lastValueFrom(this.http.post<OAuth2TokenResponseModel>(
          this.baseApiUrl + 'account/refresh-token',
          {},
          {
            headers: this.jsonWebClientHeaders,
            withCredentials: true
          }
        ));
        this.storeToken(tokenResponse);
        return true;
      } catch (err) {
        console.warn('Refresh token request failed:', err);
        this.clearStoredAuthState();
        return false;
      } finally {
        this.refreshInProgress = null;
      }
    })();
    return this.refreshInProgress;
  }

  logOut(returnUrl: string = this.router.url): void {
    this.clearStoredAuthState();
    this.http.post(
      this.baseApiUrl + 'account/logout',
      {},
      {
        headers: this.webClientHeaders,
        withCredentials: true
      }
    ).subscribe({ error: () => { } });
    this.router.navigate(['/login'], { queryParams: { returnUrl } });
  }

  hasRefreshTokenExpected(): boolean {
    return localStorage.getItem(this.refreshTokenExpectedStorageKey) === 'true';
  }

  clearStoredAuthState(): void {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('token_expiration');
    localStorage.removeItem(this.refreshTokenExpectedStorageKey);
    this.usernameSubject.next(null);
    this.rolesSubject.next([]);
    this.isAuthenticatedSubject.next(false);
  }

  sendLoginRequest(data: EmailAndPasswordModel) {
    return this.http.post<OAuth2TokenResponseModel>(
      this.baseApiUrl + 'account/login',
      data,
      {
        headers: this.webClientHeaders,
        withCredentials: true
      }
    ).pipe(tap(token => this.storeToken(token)));
  }

  sendRegisterRequest(data: RegisterRequestModel) {
    return this.http.post<AccountRegisterResponseModel>(
      this.baseApiUrl + 'account/register',
      data
    );
  }

  storeToken(token: OAuth2TokenResponseModel): boolean {
    if (!token || !token.access_token) {
      console.warn('Invalid token response passed to storeToken:', token);
      return false;
    }
    sessionStorage.setItem('access_token', token.access_token!);
    sessionStorage.setItem('token_expiration', (Date.now() + token.expires_in * 1000).toString());
    localStorage.setItem(this.refreshTokenExpectedStorageKey, 'true');
    const username = this.getUsernameFromToken(token.access_token);
    this.usernameSubject.next(username);
    this.rolesSubject.next(this.getRolesFromToken(token.access_token));
    this.isAuthenticatedSubject.next(true);
    return true;
  }

  verifyEmail(verifyEmailModel: VerifyEmailModel) {
    return this.http.post<OAuth2TokenResponseModel>(
      this.baseApiUrl + 'account/verify-email',
      verifyEmailModel,
      {
        headers: this.webClientHeaders,
        withCredentials: true
      }
    ).pipe(tap(token => this.storeToken(token)));
  }

  sendResetPasswordEmail(email: string) {
    return this.http.post(
      `${this.baseApiUrl}account/send-password-reset-email`,
      { email },
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }

  resetPassword(resetPasswordModel: ResetPasswordModel) {
    return this.http.post(
      `${this.baseApiUrl}account/password-reset`,
      resetPasswordModel,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }

  sendPhoneVerification(e164PhoneNumber: string, countryCode: string) {
    const payload = {
      phoneNumber: e164PhoneNumber,
      countryIsoCode: countryCode
    };
    return this.http.post(
      `${environment.apiUrl}user/useraccount/send-phone-verification`,
      payload,
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }

  resendEmailVerification(email: string) {
    return this.http.post(
      `${this.baseApiUrl}account/send-email-verification`,
      { email },
      {
        headers: { 'Content-Type': 'application/json' }
      }
    );
  }

  getCurrentUserProfile() {
    return this.http.get<UserProfileModel>(
      `${environment.apiUrl}user/useraccount/profile`
    );
  }

  updateUsername(username: string) {
    return this.http.put<UserProfileModel>(
      `${environment.apiUrl}user/useraccount/profile/username`,
      { username }
    );
  }

  updateEmail(email: string, currentPassword?: string) {
    return this.http.put<UserProfileModel>(
      `${environment.apiUrl}user/useraccount/profile/email`,
      { email, currentPassword }
    );
  }

  prepareExternalAuthLink(provider: string, returnUrl: string = '/account', currentPassword?: string) {
    const query = new URLSearchParams({
      provider,
      returnUrl
    });

    return this.http.post<{ redirectUrl: string }>(
      `${environment.apiUrl}user/useraccount/external-auth/link?${query.toString()}`,
      { currentPassword },
      {
        headers: this.webClientHeaders,
        withCredentials: true
      }
    );
  }

  setCurrentUsername(username: string | null): void {
    this.usernameSubject.next(username);
  }

  setPendingEmail(email: string) {
    this.pendingEmailBehaviorSubject.next(email);
  }

  getOAuthUrl(provider: string, requestedReturnUrl?: string | null) {
    requestedReturnUrl ??= this.route.snapshot.queryParamMap.get('returnUrl');
    const returnUrl = this.isSafeRelativeReturnUrl(requestedReturnUrl) ? requestedReturnUrl! : '/';
    const query = new URLSearchParams({
      provider,
      returnUrl
    });

    return `${this.oAuthStartUrl}?${query.toString()}`;
  }

  isSafeRelativeReturnUrl(returnUrl: string | null | undefined): boolean {
    return !!returnUrl &&
      returnUrl.startsWith('/') &&
      !returnUrl.startsWith('//') &&
      !returnUrl.startsWith('/\\') &&
      !returnUrl.includes('\\');
  }
}
